/**
 * RBAC Feature Flag Configuration
 *
 * Controls the enforcement of Role-Based Access Control throughout the application.
 * This affects both client-side routing/UI and server-side Firestore rules.
 */

export interface RBACConfig {
  /** Whether RBAC is enabled at all */
  enabled: boolean

  /** Whether to enforce strict role checking (vs permissive for development) */
  strictMode: boolean

  /** Whether to show debug information in the UI */
  debugMode: boolean

  /** Default roles for new users when RBAC is disabled */
  fallbackRoles: string[]
}

/**
 * Get RBAC configuration from environment variables
 */
export function getRBACConfig(): RBACConfig {
  return {
    enabled: import.meta.env.VITE_RBAC_ENABLED === 'true',
    strictMode: import.meta.env.VITE_RBAC_STRICT_MODE === 'true',
    debugMode: import.meta.env.VITE_RBAC_DEBUG === 'true',
    fallbackRoles: (import.meta.env.VITE_RBAC_FALLBACK_ROLES || 'tenantAdmin,scaleOperator').split(
      ',',
    ),
  }
}

/**
 * Check if RBAC is enabled
 */
export function isRBACEnabled(): boolean {
  return getRBACConfig().enabled
}

/**
 * Check if strict mode is enabled
 */
export function isStrictMode(): boolean {
  return getRBACConfig().strictMode
}

/**
 * Check if debug mode is enabled
 */
export function isDebugMode(): boolean {
  return getRBACConfig().debugMode
}

/**
 * Get fallback roles when RBAC is disabled
 */
export function getFallbackRoles(): string[] {
  return getRBACConfig().fallbackRoles
}

/**
 * Log RBAC configuration (only in debug mode)
 */
export function logRBACConfig(): void {
  if (isDebugMode()) {
    console.log('🔐 RBAC Configuration:', getRBACConfig())
  }
}
